/**
 * eReg Audit Trail (Phase 5 — plan §5.7)
 *
 * Site-wide, filterable view of the eReg audit trail (admin-only route).
 * Every document action — uploads, versions, views, downloads, signatures,
 * key-document pins, deletions — with the user, study, document, and a
 * compact detail summary. Paginated with Prev/Next over offset + has_more.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScrollText, ChevronLeft, ChevronRight, Loader2, X, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEreg } from '../contexts/EregContext';
import * as eregApi from '../lib/api';
import {
  PageHeader,
  RefreshButton,
  FilterBar,
  FilterSelect,
  EmptyState,
  fieldCls,
  secondaryBtnCls,
  useToast,
} from '../../ui';
import { getStudyName, sortStudiesForDisplay } from '../types';
import type { AuditEntry, User } from '../types';

const PAGE_SIZE = 50;

const ACTION_OPTIONS = [
  'uploaded',
  'version_uploaded',
  'renamed',
  'updated',
  'deleted',
  'restored',
  'viewed',
  'downloaded',
  'key_document_pinned',
  'key_document_unpinned',
  'signed',
  'signature_requested',
  'filed_from_ingest',
];

function actionLabel(action: string): string {
  return action.replace(/_/g, ' ');
}

/** Status-pill color family per action (red = destructive, amber = removals,
 *  primary = additive/uploads/pins, gray = everything else). */
function actionPillCls(action: string): string {
  if (action === 'deleted') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  }
  if (action === 'key_document_unpinned' || action.includes('archiv')) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  }
  if (
    ['uploaded', 'version_uploaded', 'filed_from_ingest', 'key_document_pinned', 'restored', 'signed'].includes(action)
  ) {
    return 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300';
}

/** Compact one-line summary of action_details, with sensible per-action shapes. */
function summarizeDetails(action: string, details?: Record<string, unknown> | null): string {
  if (!details || Object.keys(details).length === 0) return '';
  const d = details as Record<string, any>;
  if (action === 'renamed' && (d.from || d.to)) {
    return `${d.from ?? '?'} → ${d.to ?? '?'}`;
  }
  if (action === 'version_uploaded' && (d.version_number || d.version)) {
    return `v${d.version_number || d.version}`;
  }
  if (action === 'signed' && d.signature_meaning) {
    return String(d.signature_meaning);
  }
  if (action === 'filed_from_ingest' && d.original_filename) {
    return String(d.original_filename);
  }
  const json = JSON.stringify(details);
  return json.length > 80 ? `${json.slice(0, 77)}…` : json;
}

export function AuditTrail() {
  const { currentSite } = useAuth();
  const { protocols } = useEreg();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [action, setAction] = useState('');
  const [protocolId, setProtocolId] = useState('');
  const [userId, setUserId] = useState('');
  // P-13: ?document_id= deep link — "Audit history" from a document row lands
  // here pre-filtered.
  const [documentId, setDocumentId] = useState(searchParams.get('document_id') || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState<AuditEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [siteUsers, setSiteUsers] = useState<User[]>([]);
  const [documentName, setDocumentName] = useState<string | null>(null);

  const siteId = currentSite?.id;

  // User filter options: the site's members (the common "what did X do" ask).
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    eregApi.getEligibleSigners(siteId)
      .then((users) => { if (!cancelled) setSiteUsers(users); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [siteId]);

  // Resolve the filtered document's name for the chip (fail-soft: id shown).
  useEffect(() => {
    if (!documentId) {
      setDocumentName(null);
      return;
    }
    let cancelled = false;
    eregApi.getDocument(documentId)
      .then((doc) => { if (!cancelled) setDocumentName(doc.document_name); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [documentId]);

  const loadEntries = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const result = await eregApi.getAuditTrail({
        site_id: siteId,
        protocol_id: protocolId || undefined,
        document_id: documentId || undefined,
        action: action || undefined,
        user_id: userId || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(result.items || []);
      setHasMore(Boolean(result.has_more));
    } catch (err) {
      console.error('[eReg] Failed to load audit trail:', err);
      toast('Failed to load the audit trail', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [siteId, protocolId, documentId, action, userId, fromDate, toDate, offset, toast]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Filter changes restart pagination
  const setFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  const clearDocumentFilter = () => {
    setDocumentId('');
    setOffset(0);
    const next = new URLSearchParams(searchParams);
    next.delete('document_id');
    setSearchParams(next, { replace: true });
  };

  // Doc-open affordance: the audited document, one (audited) click away.
  const openDocument = async (docId: string) => {
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;
    try {
      const { url } = await eregApi.getDocumentViewUrl(docId);
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (err) {
      if (win) win.close();
      console.error('[eReg] Failed to open document:', err);
      toast('Failed to open the document', { type: 'error' });
    }
  };

  if (!currentSite) {
    return (
      <div className="mx-auto max-w-7xl">
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <ScrollText size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h2 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">No Site Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select a site from the header to view its audit trail.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title="Audit Trail"
        icon={ScrollText}
        subtitle={`Every eReg document action across ${currentSite.name} — 21 CFR Part 11 record`}
        actions={<RefreshButton onClick={loadEntries} loading={loading} />}
      />

      {/* Filters — chromeless */}
      <FilterBar>
        <FilterSelect
          value={action}
          onChange={setFilter(setAction)}
          label="Filter by action"
          auto
          options={[
            { value: '', label: 'All actions' },
            ...ACTION_OPTIONS.map((a) => ({ value: a, label: actionLabel(a) })),
          ]}
        />
        <FilterSelect
          value={protocolId}
          onChange={setFilter(setProtocolId)}
          label="Filter by study"
          auto
          options={[
            { value: '', label: 'All studies' },
            ...sortStudiesForDisplay(protocols).map((p) => ({
              value: p.id,
              label: p.study_nickname ? `${getStudyName(p)} — ${p.protocol_number}` : p.protocol_number,
            })),
          ]}
        />
        <FilterSelect
          value={userId}
          onChange={setFilter(setUserId)}
          label="Filter by user"
          auto
          options={[
            { value: '', label: 'All users' },
            ...siteUsers.map((u) => ({ value: u.id, label: u.full_name || u.email })),
          ]}
        />
        {documentId && (
          <span className="inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-600 bg-primary-50 px-2.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 sm:min-h-0 sm:py-1.5">
            <FileText size={12} />
            <span className="max-w-[14rem] truncate">{documentName || `Document ${documentId.slice(0, 8)}…`}</span>
            <button
              type="button"
              onClick={clearDocumentFilter}
              aria-label="Clear document filter"
              className="rounded-full p-0.5 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-primary-900/40"
            >
              <X size={12} />
            </button>
          </span>
        )}
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setOffset(0); }}
            aria-label="From date"
            className={`${fieldCls} min-h-[44px] sm:min-h-0`}
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          To
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setOffset(0); }}
            aria-label="To date"
            className={`${fieldCls} min-h-[44px] sm:min-h-0`}
          />
        </label>
      </FilterBar>

      {/* Entries */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          variant="card"
          title="No audit entries"
          hint="No activity matches these filters — try widening the date range or clearing the action filter."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Study</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Document</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((entry) => {
                  const detail = summarizeDetails(entry.action, entry.action_details);
                  return (
                    <tr key={entry.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                        {new Date(entry.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        <span className="block max-w-[12rem] truncate" title={entry.user?.email || entry.user_email || undefined}>
                          {entry.user?.full_name || entry.user_email || '—'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${actionPillCls(entry.action)}`}
                        >
                          {actionLabel(entry.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {entry.protocol ? (
                          <span
                            className="block max-w-[12rem] truncate"
                            title={entry.protocol.study_nickname || entry.protocol.protocol_number}
                          >
                            {entry.protocol.protocol_number}
                            {entry.protocol.study_nickname && (
                              <span className="text-gray-400 dark:text-gray-500"> — {entry.protocol.study_nickname}</span>
                            )}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {entry.document && entry.document_id ? (
                          <button
                            type="button"
                            onClick={() => openDocument(entry.document_id!)}
                            title={`Open ${entry.document.document_name}`}
                            className="block max-w-[14rem] truncate text-left transition-colors duration-150 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-primary-400"
                          >
                            {entry.document.document_name}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {detail ? (
                          <span
                            className="block max-w-[16rem] truncate"
                            title={entry.action_details ? JSON.stringify(entry.action_details) : undefined}
                          >
                            {detail}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
            <p className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
              Showing {offset + 1}–{offset + items.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
                disabled={offset === 0 || loading}
                className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <ChevronLeft size={14} />
                Prev
              </button>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!hasMore || loading}
                className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditTrail;
